import { pluralizeOutcomeNoun } from "@repo/app-types/entities/outcome";
import {
  computeCriteriaOutcomeImpactLevel,
  computeCriteriaOutcomeImpactScore,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  getOutcomeImpactCopy,
  type OutcomeImpactCopy,
} from "@repo/app-types/helpers/outcomeImpactCopy";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

interface OutcomePotentialComparisonCardProps {
  /** Omitted for `new_criterion` proposals — card renders proposed-only copy. */
  readonly baselineAuc?: number;
  readonly testedAuc: number;
  /**
   * For revenue outcomes pass `net_revenue` (per 1k opps); for non-revenue
   * outcomes pass `net_conversions` (per 1k calls). Both can be null when the
   * side has no scored calls — in that case the "coaching potential" half is
   * suppressed for that side. Baseline is omitted entirely for new-criterion
   * proposals.
   */
  readonly baselineNetConversions?: number | null;
  readonly testedNetConversions: number | null;
  readonly averageRevenuePerOpp: number | undefined;
  /** Noun form of the outcome (e.g., "enrollment", "Inforce policy"). */
  readonly outcomeNoun: string | undefined;
  /** Per-playbook plural noun override (see `getOutcomePluralNoun`). */
  readonly outcomePluralNoun?: string | undefined;
  /** Whether this is the revenue outcome (see `isRevenueOutcomeDefinition`). */
  readonly isRevenueOutcome?: boolean | undefined;
}

interface ImpactStyle {
  level: string;
  textColor: string;
}

const getImpactStyle = (impactScore: number): ImpactStyle => {
  const level = computeCriteriaOutcomeImpactLevel(impactScore);
  switch (level) {
    case "very_high":
      return { level: "Very high", textColor: "text-green-700" };
    case "high":
      return { level: "High", textColor: "text-success" };
    case "medium":
      return { level: "Medium", textColor: "text-lime-600" };
    case "low":
      return { level: "Low", textColor: "text-gray-600" };
    case "very_low":
    default:
      return { level: "Very low", textColor: "text-muted-foreground" };
  }
};

/**
 * `getOutcomeImpactCopy` falls back to the bare "increase revenue" sentence
 * for revenue outcomes when `averageRevenuePerOpp` is missing, which gives
 * us nothing worth comparing. Treat that as no copy at all.
 */
const isInformativeCopy = (copy: OutcomeImpactCopy | null): boolean => {
  if (!copy) return false;
  if (copy.isRevenue && copy.sentence === "increase revenue") return false;
  return true;
};

export const OutcomePotentialComparisonCard: FC<
  OutcomePotentialComparisonCardProps
> = ({
  baselineAuc,
  testedAuc,
  baselineNetConversions,
  testedNetConversions,
  averageRevenuePerOpp,
  outcomeNoun,
  outcomePluralNoun,
  isRevenueOutcome,
}) => {
  const testedImpact = getImpactStyle(
    computeCriteriaOutcomeImpactScore(testedAuc),
  );
  const baselineImpact =
    baselineAuc !== undefined
      ? getImpactStyle(computeCriteriaOutcomeImpactScore(baselineAuc))
      : null;

  const baselineCopy =
    baselineNetConversions != null
      ? getOutcomeImpactCopy({
          netConversions: baselineNetConversions,
          averageRevenuePerOpp,
          outcomeNoun,
          outcomePluralNoun,
          isRevenueOutcome,
        })
      : null;
  const testedCopy =
    testedNetConversions !== null
      ? getOutcomeImpactCopy({
          netConversions: testedNetConversions,
          averageRevenuePerOpp,
          outcomeNoun,
          outcomePluralNoun,
          isRevenueOutcome,
        })
      : null;

  const baselineDescriptor = getCoachingDescriptor(
    baselineNetConversions ?? null,
    baselineCopy,
    averageRevenuePerOpp,
    outcomeNoun,
    outcomePluralNoun,
  );
  const testedDescriptor = getCoachingDescriptor(
    testedNetConversions,
    testedCopy,
    averageRevenuePerOpp,
    outcomeNoun,
    outcomePluralNoun,
  );

  const impactSentence = baselineImpact
    ? renderImpactSentence(baselineImpact, testedImpact)
    : renderProposedImpactSentence(testedImpact, outcomeNoun);
  const coachingSentence = renderCoachingSentence({
    baseline: baselineDescriptor,
    tested: testedDescriptor,
    hasBaseline: baselineImpact !== null,
  });

  return (
    <div className="rounded-md bg-white px-4 pt-3 pb-4 shadow-sm ring-1 ring-black/5">
      <Label size={Size.SMALL} className="mb-2">
        Outcome potential
      </Label>
      <p className="text-xs text-gray-600">
        {impactSentence}
        {coachingSentence ? <>, {coachingSentence}</> : null}.
      </p>
    </div>
  );
};

function renderProposedImpactSentence(
  tested: ImpactStyle,
  outcomeNoun: string | undefined,
): ReactNode {
  return (
    <>
      This criterion has a{" "}
      <span className={twMerge("font-semibold lowercase", tested.textColor)}>
        {tested.level}
      </span>{" "}
      impact{outcomeNoun ? <> on {outcomeNoun}</> : null}
    </>
  );
}

type CoachingDescriptor =
  | { kind: "informative"; sentence: string }
  | { kind: "minimal"; sentence: string };

/**
 * Per-side coaching sentence: the informative `+N noun per 1k calls` copy
 * when the side has positive impact AND the displayed magnitude rounds to
 * a non-zero value; otherwise the short-form "unlikely to significantly
 * increase {pluralNoun}" fallback. `null` when the side has no scored
 * calls — the coaching half is suppressed entirely in that case.
 */
function getCoachingDescriptor(
  netValue: number | null,
  copy: OutcomeImpactCopy | null,
  averageRevenuePerOpp: number | undefined,
  outcomeNoun: string | undefined,
  outcomePluralNoun: string | undefined,
): CoachingDescriptor | null {
  if (netValue === null) return null;
  const isMinimal =
    !isInformativeCopy(copy) ||
    displaysAsZero(netValue, copy!.isRevenue, averageRevenuePerOpp);
  if (isMinimal) {
    return {
      kind: "minimal",
      sentence: `unlikely to significantly increase ${pluralizeOutcomeNoun(
        outcomeNoun ?? "conversion",
        outcomePluralNoun,
      )}`,
    };
  }
  return { kind: "informative", sentence: copy!.sentence };
}

/**
 * `getOutcomeImpactCopy` rounds the displayed magnitude when it formats
 * the sentence — non-revenue uses `maximumFractionDigits: 0`, revenue uses
 * a 1-decimal percent. A raw `netValue` like `0.3` therefore renders as
 * `+0` (or `0.0%`) which reads as no impact, so we should fall back to
 * the short-form copy in those cases instead of showing a zero-looking
 * informative sentence.
 */
function displaysAsZero(
  netValue: number,
  isRevenue: boolean,
  averageRevenuePerOpp: number | undefined,
): boolean {
  if (!isRevenue) {
    return Math.round(netValue) <= 0;
  }
  if (averageRevenuePerOpp === undefined || averageRevenuePerOpp <= 0) {
    return true;
  }
  const revenuePerOpp = netValue / 1000;
  const revenueChangePercent = (revenuePerOpp / averageRevenuePerOpp) * 100;
  return revenueChangePercent < 0.05;
}

function renderCoachingSentence(options: {
  baseline: CoachingDescriptor | null;
  tested: CoachingDescriptor | null;
  hasBaseline: boolean;
}): ReactNode {
  const { baseline, tested, hasBaseline } = options;

  if (hasBaseline) {
    if (!baseline || !tested) return null;
    if (baseline.sentence === tested.sentence) {
      return (
        <>
          and coaching potential did not change, still{" "}
          <span className="font-semibold text-success">{tested.sentence}</span>
        </>
      );
    }
    return (
      <>
        and coaching potential changed from{" "}
        <span className="font-semibold text-success">{baseline.sentence}</span>{" "}
        to <span className="font-semibold text-success">{tested.sentence}</span>
      </>
    );
  }

  if (!tested) return null;
  const verb = tested.kind === "informative" ? "can" : "is";
  return (
    <>
      and coaching on this criterion {verb}{" "}
      <span className="font-semibold text-success">{tested.sentence}</span>
    </>
  );
}

function renderImpactSentence(
  baseline: ImpactStyle,
  tested: ImpactStyle,
): ReactNode {
  if (baseline.level === tested.level) {
    return (
      <>
        Criteria impact did not change, still{" "}
        <span className={twMerge("font-semibold", tested.textColor)}>
          {tested.level}
        </span>
      </>
    );
  }
  return (
    <>
      Criteria impact changed from{" "}
      <span className={twMerge("font-semibold", baseline.textColor)}>
        {baseline.level}
      </span>{" "}
      to{" "}
      <span className={twMerge("font-semibold", tested.textColor)}>
        {tested.level}
      </span>
    </>
  );
}
